import { isMilestoneScheduleEndDate } from '@sequencehq/utils'
import { BillingSchedule } from 'modules/Customer/hooks/useFetchBillingSchedules'
import { validateCombinedSchedule } from 'modules/Cube/view/layouts/quote/QuoteEditorHeader/executeQuote/utils/validateCombinedSchedule'

/** The combined-schedule validation of a schedule, appended as-is and with a reset. */
export type ScheduleValidation = {
  /** The combined schedule validated with the quote's phases appended as-is. */
  asIs: ReturnType<typeof validateCombinedSchedule>
  /** The same, but with the first appended phase resetting its billing period. */
  withReset: ReturnType<typeof validateCombinedSchedule>
}

/**
 * An eligible renewal-target schedule with its applicability pre-computed, so the
 * selection hook and UI can read these facts directly rather than re-deriving
 * them for the selected schedule.
 */
export type EligibleSchedule = {
  schedule: BillingSchedule
  /** No fixed end date — the renewal anchors to a chosen date rather than renewing on. */
  isOpenEnded: boolean
  /** Milestone-based — has no real end date to append after and can't be renewed. */
  isMilestone: boolean
  /** The quote's phases can be appended as-is without proration errors. */
  isValidAsIs: boolean
  /** ...or become valid once the first appended phase resets its billing period. */
  isValidWithReset: boolean
  /** Valid only with a reset: appending as-is would misalign the billing period. */
  resetRequired: boolean
  /** Whether the schedule can take the renewal at all (not milestone, and valid
   * open-ended / as-is / with a reset). A non-viable fixed-end schedule is one
   * that's invalid even with a reset. */
  isViableSchedule: boolean
  /** The validation messages that survive a reset, shown as the reason a
   * fixed-end schedule isn't viable. */
  blockedValidationMessages: string[]
}

const isOpenEndedSchedule = (schedule: BillingSchedule): boolean =>
  !schedule.endDate

const isMilestoneSchedule = (schedule: BillingSchedule): boolean =>
  isMilestoneScheduleEndDate(schedule.endDate ?? '')

/**
 * Pre-computes a schedule's renewal applicability from its shape and the
 * combined-schedule validation. A milestone schedule is never viable; every
 * other schedule is viable when the quote's phases combine validly either as-is
 * or with a billing-period reset. The validation closes an open-ended schedule's
 * final phase at the renewal date, so open-ended schedules are validated by the
 * same proration check as fixed-end ones. An absent validation (still loading)
 * is treated as valid so schedules aren't prematurely marked non-viable.
 */
export const deriveScheduleMetadata = ({
  schedule,
  validation
}: {
  schedule: BillingSchedule
  validation: ScheduleValidation | undefined
}): EligibleSchedule => {
  const isOpenEnded = isOpenEndedSchedule(schedule)
  const isMilestone = isMilestoneSchedule(schedule)
  const isValidAsIs = !validation?.asIs.isInvalid
  const isValidWithReset = !validation?.withReset.isInvalid

  const isViableSchedule = !isMilestone && (isValidAsIs || isValidWithReset)
  // A reset is needed when appending as-is would be invalid but a reset makes
  // it valid. This applies uniformly to open-ended schedules too, because their
  // validation closes the open phase at the renewal date, so the standard
  // proration check sees the real billing boundary. Milestone / non-viable
  // schedules can't be renewed, so never reset.
  const resetRequired = isViableSchedule && !isValidAsIs

  return {
    schedule,
    isOpenEnded,
    isMilestone,
    isValidAsIs,
    isValidWithReset,
    resetRequired,
    isViableSchedule,
    blockedValidationMessages: validation?.withReset.validationMessages ?? []
  }
}
